import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  FormControl,
  InputAdornment,
  Link,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useLazyQuery, useMutation } from "@apollo/client";
import LoadingToast from "../../../../components/LoadingToast";
import {
  GET_REMEDIATION_PLAN,
  SAVE_REMEDIATION_INPUTS,
} from "../../../../apis/organization-apis/Infra-apis/InfraApis";

// Rendering every resource at once is unusable for checks that fail in the
// thousands, so page through them.
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const SAVE_DEBOUNCE_MS = 800;

// Steps are authored with **bold** and [text](url); handle just those two.
const renderInlineMarkdown = (text = "") => {
  const parts = [];
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));

    if (match[1]) {
      parts.push(
        <Box key={parts.length} component="strong" sx={{ fontWeight: 600 }}>
          {match[1]}
        </Box>
      );
    } else {
      parts.push(
        <Link
          key={parts.length}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          {match[2]}
        </Link>
      );
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
};

// `run` passes each word as a separate argument, so a substituted value holding
// JSON, braces or spaces has to arrive as one quoted word. Anything the recipe
// already quoted is left alone.
// Mirrors substitute() in RemediationCommand.js: a value holding spaces, braces
// or quotes -- a JSON document, say -- has to reach the shell as one word.
// Values coming from a dropdown are substituted here rather than server-side,
// so the same rule has to apply.
const NEEDS_QUOTING = /[\s"'{}$&|;<>()]/;

const shellValue = (value) =>
  NEEDS_QUOTING.test(value)
    ? `'${value.replace(/'/g, `'\\''`)}'`
    : value;

// The command already carries whatever quoting its values need, so wrap the
// whole line as ONE shell word and eval it -- that keeps the inner quoting
// intact and still lets a recipe chain two calls with &&.
const asShellWord = (command = "") =>
  `'${String(command).replace(/'/g, `'\\''`)}'`;

// Identity for one row. resourceId is NOT unique across accounts -- a Cloud SQL
// resourceId is the bare instance name, and the same name routinely exists in a
// dev and a prod project. Keying selection or per-resource inputs on it alone
// makes those two rows one: tick either checkbox and both light up. The server
// sends resourceKey ("<accountId>::<resourceId>"); fall back to resourceId so an
// older API still renders.
const idOf = (resource) => resource?.resourceKey || resource?.resourceId;

// Split a composite key back into the parts the save mutation needs.
const partsOf = (key) => {
  const i = String(key).indexOf("::");
  return i === -1
    ? { accountId: null, resourceId: String(key) }
    : { accountId: String(key).slice(0, i) || null, resourceId: String(key).slice(i + 2) };
};

/**
 * Shows ready-to-run CLI commands for a check's failing resources.
 *
 * One command per resource: values differ across resources of the same check
 * (two GuardDuty findings differ only by region), so a single template with
 * placeholders would still leave the user editing each one by hand.
 *
 * Variables the scan cannot supply are collected once at the top and
 * substituted into every command as the user types.
 */
const RemediationDrawer = ({ open, onClose, checkId, resourceIds = [] }) => {
  const theme = useTheme();

  // Shared across every resource, and per-resource overrides for variables the
  // user has marked as differing.
  const [inputs, setInputs] = useState({});
  const [perResource, setPerResource] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
 
  const [accountFilter, setAccountFilter] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [saveState, setSaveState] = useState(null);
  const saveResetRef = useRef(null);
  // Commands carrying a warning stay hidden until the user acknowledges it.
  // Reset whenever the drawer closes, so acceptance is never carried over to
  // another check or another visit.
  const [warningAccepted, setWarningAccepted] = useState(false);
  // Which row's command was last copied, so only that button shows the tick.
  const [copiedKey, setCopiedKey] = useState(null);
  const copyResetRef = useRef(null);

  const [fetchPlan, { data, loading, error }] = useLazyQuery(
    GET_REMEDIATION_PLAN,
    { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true }
  );
  const [saveInputs] = useMutation(SAVE_REMEDIATION_INPUTS);

  // `resourceIds` is a new array identity on each parent render, so depend on a
  // stable key instead -- otherwise this refetches on every parent re-render.
  const resourceIdsKey = resourceIds.join(",");

  useEffect(() => {
    if (!open || !checkId) return;
    fetchPlan({
      variables: { check_id: checkId, resource_ids: resourceIds },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, checkId, resourceIdsKey, fetchPlan]);

  useEffect(() => {
    if (open) return;
    setInputs({});
    setPerResource({});
    setSelected(new Set());
    setSearch("");
    setAccountFilter("");
    setDownloading(false);
    setPendingDownload(null);
    setPage(1);
    setRowsPerPage(ROWS_PER_PAGE_OPTIONS[0]);
    setSaveState(null);
    setWarningAccepted(false);
    setCopiedKey(null);
  }, [open]);

  useEffect(
    () => () => {
      clearTimeout(saveResetRef.current);
      clearTimeout(copyResetRef.current);
    },
    []
  );

  // Ignore the cached result while a fetch is in flight: on reopen that result
  // belongs to the previous session and would flash stale commands.
  const plan = loading ? null : data?.getRemediationPlan;

  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    const saved = plan?.savedInputs;
    if (!saved) return;
    seededRef.current = true;
    const valid = new Set((plan?.resources || []).map((r) => idOf(r)));
    const clean = {};
    for (const [id, values] of Object.entries(saved.perResource || {})) {
      if (valid.has(id)) clean[id] = values;
    }
    setPerResource(clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  // Every variable the user supplies: still-missing ones, plus any that only
  // look filled because a saved value was applied. Without the second group a
  // saved value would be invisible and uneditable on reopen.
  const promptedVariables = useMemo(() => {
    if (!plan?.hasCommand) return [];
    const saved = plan.savedInputs || {};
    const byKey = new Map();

    for (const item of plan.missing || []) byKey.set(item.key, item);
    for (const resource of plan.resources || []) {
      for (const item of resource.missing || []) {
        if (!byKey.has(item.key)) byKey.set(item.key, item);
      }
    }
    for (const values of Object.values(saved.perResource || {})) {
      for (const key of Object.keys(values)) {
        if (!byKey.has(key)) byKey.set(key, { key });
      }
    }

    // Order by where each placeholder sits in the command, so the fields read
    // left to right in the same sequence as the flags they fill. Insertion
    // order alone reshuffles as values are saved and drop out of `missing`.
    // Uses whichever resource still has the most placeholders left.
    const keys = [...byKey.keys()];
    const template = (plan.resources || []).reduce((best, r) => {
      const count = keys.filter((k) =>
        (r.command || "").toLowerCase().includes(`<${k.toLowerCase()}>`)
      ).length;
      return count > best.count ? { count, text: r.command || "" } : best;
    }, { count: -1, text: "" }).text;

    const positionOf = (key) => {
      const at = template.toLowerCase().indexOf(`<${key.toLowerCase()}>`);
      return at === -1 ? Number.MAX_SAFE_INTEGER : at;
    };
    return [...byKey.values()].sort(
      (a, b) => positionOf(a.key) - positionOf(b.key) || a.key.localeCompare(b.key)
    );
  }, [plan]);

  // fetchChoices is keyed by account, then variable, because a dropdown built
  // from one account's resources is meaningless against another's. "*" holds
  // static lists that do not depend on an account.
  const choicesFor = (key, accountId, resourceKey) => {
    const byAccount = plan?.fetchChoices || {};
    if (resourceKey && byAccount[resourceKey]?.[key]) {
      return byAccount[resourceKey][key];
    }
    if (accountId && byAccount[accountId]?.[key]) {
      return byAccount[accountId][key];
    }
    return byAccount["*"]?.[key] || [];
  };

  const hintFor = (resourceId, key) =>
    plan?.fetchHints?.[String(resourceId).split("#")[0]]?.[key] || "";

  // Where every resource reports the same reason a value is unresolved, it is
  // one fact about the account, not one per row.
  const sharedHintFor = (key) => {
    const scope = (plan?.resources || []).filter(
      (r) => !accountFilter || r.accountId === accountFilter
    );
    if (!scope.length) return "";
    const all = scope
      .map(
        (r) =>
          plan?.fetchHints?.[String(r.resourceId).split("#")[0]]?.[key]
      )
      .filter(Boolean);
    return all.length === scope.length && new Set(all).size === 1
      ? all[0]
      : "";
  };

  // Which fields each resource shows. Built from the plan, never from what is
  // currently unfilled -- otherwise a field unmounts the moment its own first
  // character is typed, and disappears again once the value is saved.
  const fieldsByResource = useMemo(() => {
    const map = {};
    const saved = plan?.savedInputs || {};
    // Ordered by promptedVariables so a field keeps its position once filled --
    // building from `missing` alone reshuffles the row as values land.
    const order = promptedVariables.map((item) => item.key);

    for (const resource of plan?.resources || []) {
      const byKey = new Map();
      for (const item of resource.missing || []) byKey.set(item.key, item);
      for (const key of Object.keys(
        saved.perResource?.[idOf(resource)] || {}
      )) {
        if (!byKey.has(key)) byKey.set(key, { key });
      }
      for (const key of Object.keys(
        plan?.fetchChoices?.[resource.accountId] || {}
      )) {
        if (!byKey.has(key)) byKey.set(key, { key });
      }
      map[idOf(resource)] = order
        .filter((key) => byKey.has(key))
        .map((key) => byKey.get(key));
    }
    return map;
  }, [plan, promptedVariables]);

  const resources = useMemo(() => {
    return (plan?.resources || []).map((resource) => {
      // A per-resource value always wins over the shared one.
      const values = {
        ...inputs,
        ...(perResource[idOf(resource)] || {}),
      };
      const entries = Object.entries(values).filter(
        ([, value]) => (value || "").trim()
      );
      if (!entries.length) return resource;

      let command = resource.command;
      const answered = new Set();
      for (const [key, value] of entries) {
        const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const pattern = new RegExp(`(['"]?)<${escaped}>`, "gi");
        if (pattern.test(command)) {
          command = command.replace(pattern, (match, quote) =>
            quote ? `${quote}${value.trim()}` : shellValue(value.trim())
          );
          answered.add(key);
        }
      }

      const missing = (resource.missing || []).filter(
        (item) => !answered.has(item.key)
      );
      return { ...resource, command, missing, isComplete: missing.length === 0 };
    });
  }, [plan, inputs, perResource]);

  // Every account this check fails in, in first-seen order.
  const allAccounts = useMemo(
    () => [...new Set(resources.map((r) => r.accountId).filter(Boolean))],
    [resources]
  );

  // With more than one account, start on the first rather than showing a mixed
  // list whose bulk bar cannot serve any of them.
  useEffect(() => {
    if (allAccounts.length > 1 && !allAccounts.includes(accountFilter)) {
      setAccountFilter(allAccounts[0]);
    }
    if (allAccounts.length <= 1 && accountFilter) setAccountFilter("");
  }, [allAccounts, accountFilter]);

  // The bulk bar edits whichever account is currently filtered to. With a
  // single account there is no filter, so fall back to that account.
  const bulkAccount = accountFilter || allAccounts[0] || "";

  const isGcp = plan?.cloud === "gcp";
  const accountWord = isGcp ? "project" : "account";
  const accountWordPlural = isGcp ? "projects" : "accounts";

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return resources.filter((resource) => {
      if (accountFilter && resource.accountId !== accountFilter) return false;
      if (!term) return true;
      return (
        (resource.resourceName || "").toLowerCase().includes(term) ||
        (resource.resourceId || "").toLowerCase().includes(term)
      );
    });
  }, [resources, search, accountFilter]);

  const pageCount = Math.ceil(filtered.length / rowsPerPage) || 1;
  // Filtering can shrink the list past the current page, which would render an
  // empty body with no way back.
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage
  );
  // Per-account readiness, so the selector shows where work remains instead of
  // making the user open each account to find out.
  const readinessByAccount = useMemo(() => {
    const map = {};
    for (const resource of resources) {
      const key = resource.accountId || "";
      const entry = (map[key] = map[key] || { ready: 0, total: 0 });
      entry.total += 1;
      if (resource.isComplete) entry.ready += 1;
    }
    return map;
  }, [resources]);

  // Scoped to the visible account: the list shows one account, so a count
  // spanning all of them reads as a bug.
  const readyCount = filtered.filter((r) => r.isComplete).length;
  const totalReady = resources.filter((r) => r.isComplete).length;

  // Autosave, debounced so typing does not fire a request per keystroke. The
  // first pass after open is skipped -- the empty initial state would otherwise
  // overwrite the values just loaded.
  const hydratedRef = useRef(false);
  useEffect(() => {
    hydratedRef.current = false;
  }, [open, checkId]);

  useEffect(() => {
    if (!open || !checkId || !plan?.hasCommand) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSaveState("saving");
        for (const [account, values] of Object.entries(inputs)) {
          if (!Object.keys(values || {}).length) continue;
          for (const target of resources) {
            if ((target.accountId || "") !== (account || "")) continue;
            if (perResource[idOf(target)]) continue;
            await saveInputs({
              variables: {
                check_id: checkId,
                resource_id: target.resourceId,
                account_id: target.accountId || null,
                inputs: values,
              },
            });
          }
        }
        // perResource is keyed by "<accountId>::<resourceId>"; the mutation
        // wants the two separately so the row is scoped to the right account.
        // The key carries the account, so no lookup by resourceId is needed --
        // that lookup returned the wrong row when two accounts shared a name.
        for (const [key, values] of Object.entries(perResource)) {
          if (!key || !Object.keys(values).length) continue;
          const { accountId, resourceId } = partsOf(key);
          await saveInputs({
            variables: {
              check_id: checkId,
              resource_id: resourceId,
              account_id: accountId,
              inputs: values,
            },
          });
        }
        setSaveState("saved");
        clearTimeout(saveResetRef.current);
        saveResetRef.current = setTimeout(() => setSaveState(null), 2000);
      } catch (err) {
        console.error("Failed to save remediation values:", err);
        setSaveState("error");
      }
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, perResource, open, checkId, plan?.hasCommand]);

  // Bulk edit writes straight into the selected resources' own values, so the
  // inline field for each shows what was applied and stays editable.
  const applyToSelected = (key, value) => {
    setPerResource((prev) => {
      const updated = { ...prev };
      for (const id of selected) {
        updated[id] = { ...(updated[id] || {}), [key]: value };
      }
      return updated;
    });
  };

  // Shows the value only where every selected resource agrees; a mix stays
  // blank so applying one does not silently overwrite the others.
  const bulkValueFor = (key) => {
    const values = [...selected].map(
      (id) => perResource[id]?.[key] ?? ""
    );
    if (!values.length) return "";
    return new Set(values).size === 1 ? values[0] : "";
  };

  const clearSelectedValues = () => {
    setPerResource((prev) => {
      const updated = { ...prev };
      for (const id of selected) {
        if (!updated[id]) continue;
        const next = { ...updated[id] };
        for (const item of promptedVariables) delete next[item.key];
        if (Object.keys(next).length) updated[id] = next;
        else delete updated[id];
      }
      return updated;
    });
  };

  const toggleSelected = (resourceId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  };

  // Distinct accounts across the filtered resources, in first-seen order. A
  // Covers every filtered resource for one account, not just the page on
  // screen. Passing no account builds from everything (single-account case).
  const buildScript = (account = null) => {
    let scope = account
      ? resources.filter((r) => r.accountId === account)
      : filtered;
    if (selected.size > 0) {
      const inSelection = scope.filter((r) => selected.has(idOf(r)));
      if (inSelection.length) scope = inSelection;
    }
    const ready = scope.filter((r) => r.isComplete);
    const skipped = scope.filter((r) => !r.isComplete);
    const label = account || scope.find((r) => r.accountId)?.accountId;
    const slug = String(plan?.checkType || `check-${checkId}`).replace(
      /[^A-Za-z0-9._-]/g,
      "_"
    );
    const fileName = label
      ? `remediate-${slug}-${String(label).replace(/[^A-Za-z0-9._-]/g, "_")}.sh`
      : `remediate-${slug}.sh`;

    const lines = [
      "#!/usr/bin/env bash",
      "# Infrastructure remediation",
      `# Generated by Regodit on ${new Date().toISOString().slice(0, 10)} - ${
        ready.length
      } resource${ready.length === 1 ? "" : "s"}`,
      label ? `# Requires credentials for ${accountWord} ${label}` : null,
      allAccounts.length > 1
        ? `# This check spans ${allAccounts.length} ${accountWordPlural} - one script each. Run this one against ${label} only.`
        : null,
      "#",
      "# HOW TO RUN",
      `#   1. Authenticate to the ${accountWord} named above.`,
      `#   2. chmod +x ${fileName}`,
      `#   3. ./${fileName}`,
      "#",
      "# Step 1 prints every command without running anything.",
      "# Step 2 runs them, but only after you answer 'y'.",
      "",
      "set -uo pipefail",
      "failed=0",
      "names=()",
      "cmds=()",
      "",
      "queue() {",
      '  names+=("$1")',
      '  cmds+=("$2")',
      "}",
      "",
      "run_all() {",
      '  local i',
      '  for i in "${!cmds[@]}"; do',
      '    echo "==> ${names[$i]}"',
      '    # eval, not "$@": a recipe may chain two calls with &&.',
      '    if eval "${cmds[$i]}"; then',
      '      echo "    ok"',
      "    else",
      '      echo "    FAILED"',
      "      failed=1",
      "    fi",
      "  done",
      "}",
      "",
    ].filter((line) => line !== null);

    if (skipped.length) {
      lines.push(
        `# ---- ${skipped.length} command(s) below are INCOMPLETE ----`
      );
      lines.push(
        "# Fill the <PLACEHOLDER> values, then uncomment the queue lines."
      );
      lines.push("");
      for (const resource of skipped) {
        const keys = (resource.missing || []).map((m) => m.key).join(", ");
        const name = (resource.resourceName || resource.resourceId || "").replace(
          /"/g,
          '\\"'
        );
        lines.push(`# ${name}${keys ? ` -- missing: ${keys}` : ""}`);
        lines.push(`# queue "${name}" \\`);
        lines.push(`#   ${asShellWord(resource.command || "")}`);
        lines.push("");
      }
    }

    for (const resource of ready) {
      const name = (resource.resourceName || resource.resourceId || "").replace(
        /"/g,
        '\\"'
      );
      lines.push(`queue "${name}" \\`);
      lines.push(`  ${asShellWord(resource.command)}`);
      lines.push("");
    }

    lines.push('echo "The following ${#cmds[@]} command(s) will run' + (label ? ` against ${label}` : "") + ':"');
    lines.push("echo");
    lines.push('for i in "${!cmds[@]}"; do');
    lines.push('  echo "  ${names[$i]}"');
    lines.push("  printf '    %s\\n\\n' \"${cmds[$i]}\"");
    lines.push("done");
    lines.push("");
    lines.push('if [ ! -t 0 ]; then');
    lines.push('  echo "No terminal attached - run this script directly so it can ask for confirmation."');
    lines.push("  exit 1");
    lines.push("fi");
    lines.push('printf "Run these now? [y/N] "');
    lines.push('reply=""');
    lines.push("read -r reply");
    lines.push('case "$reply" in');
    lines.push("  [yY]|[yY][eE][sS]) ;;");
    lines.push('  *) echo "Aborted. Nothing was run."; exit 0 ;;');
    lines.push("esac");
    lines.push("");
    lines.push("echo");
    lines.push("run_all");
    lines.push("");
    lines.push('if [ "$failed" -ne 0 ]; then');
    lines.push('  echo "One or more commands failed."');
    lines.push("fi");
    lines.push('exit "$failed"');
    lines.push("");

    return lines.join("\n");
  };

  const copyCommand = async (resource) => {
    try {
      await navigator.clipboard.writeText(resource.command || "");
    } catch {
      return; // clipboard blocked (insecure origin, denied permission)
    }
    setCopiedKey(idOf(resource));
    clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setCopiedKey(null), 1500);
  };

  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const safeName = (value) => String(value).replace(/[^A-Za-z0-9._-]/g, "_");

  // One script per account -- each needs its own credentials, so a single file
  // mixing them cannot be run.
  const scriptSlug = safeName(plan?.checkType || `check-${checkId}`);

  const saveForAccount = (account) => {
    const contents = buildScript(account);
    const name = account
      ? `remediate-${scriptSlug}-${safeName(account)}.sh`
      : `remediate-${scriptSlug}.sh`;
    saveBlob(new Blob([contents], { type: "text/x-shellscript" }), name);
  };

  const downloadCurrentAccount = () => {
    // Defence in depth: the button is already hidden while a warning is
    // unaccepted, but never build a script for a command the user has not been
    // shown the consequences of.
    if (plan?.warning && !warningAccepted) return;
    saveForAccount(accountFilter || allAccounts[0] || null);
  };

  const gapsFor = (accounts) =>
    accounts
      .map((account) => {
        const scope = resources.filter((r) =>
          account ? r.accountId === account : true
        );
        const pool =
          selected.size > 0
            ? scope.filter((r) => selected.has(idOf(r)))
            : scope;
        const missing = pool.filter((r) => !r.isComplete);
        if (!missing.length) return null;
        const keys = [
          ...new Set(
            missing.flatMap((r) => (r.missing || []).map((m) => m.key))
          ),
        ];
        return { account, count: missing.length, keys };
      })
      .filter(Boolean);

  const requestDownload = (scopeMode) => {
    const accounts =
      scopeMode === "all"
        ? allAccounts
        : [accountFilter || allAccounts[0] || null];
    const gaps = gapsFor(accounts);
    if (!gaps.length) {
      if (scopeMode === "all" && allAccounts.length > 1) downloadAllAccounts();
      else downloadCurrentAccount();
      return;
    }
    setPendingDownload({ scopeMode, gaps });
  };

  const confirmDownload = () => {
    const mode = pendingDownload?.scopeMode;
    setPendingDownload(null);
    if (mode === "all" && allAccounts.length > 1) downloadAllAccounts();
    else downloadCurrentAccount();
  };

  // Accounts that would produce at least one runnable command, so an account
  // with nothing ready does not download an empty script.
  const accountScope = (account) => {
    const scope = resources.filter((r) => r.accountId === account);
    return selected.size > 0
      ? scope.filter((r) => selected.has(idOf(r)))
      : scope;
  };

  const accountsWithWork = allAccounts.filter((account) =>
    accountScope(account).some((r) => r.isComplete)
  );



  const downloadAllAccounts = async () => {
    if (plan?.warning && !warningAccepted) return;
    if (accountsWithWork.length <= 1) {
      saveForAccount(accountsWithWork[0] || accountFilter || null);
      return;
    }
    setDownloading(true);
    try {
      for (let i = 0; i < accountsWithWork.length; i += 1) {
        saveForAccount(accountsWithWork[i]);
        // Browsers drop downloads fired in the same tick.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    } finally {
      setDownloading(false);
    }
  };

  const monospace = {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, "Courier New", monospace',
    fontSize: 13,
    lineHeight: 1.7,
  };

  const consoleSteps = plan?.consoleSteps || [];

  // A check whose command has a side effect past the resource it fixes carries a
  // warning the user must read first. Until they accept it, the commands, the
  // inputs and the download are all withheld -- the download button lives in the
  // sticky footer outside renderBody, so gating renderBody alone would leave it
  // live next to the unread warning.
  const warningPending = Boolean(plan?.warning) && !warningAccepted;
  const hasCommands =
    Boolean(plan?.hasCommand) && resources.length > 0 && !warningPending;

  // Only label rows with their account when more than one is present -- a
  // single-account check does not need the noise.
  const multipleAccounts =
    new Set((plan?.resources || []).map((r) => r.accountId).filter(Boolean))
      .size > 1;

  const renderConsoleSteps = ({ collapsible }) => {
    if (!consoleSteps.length) return null;

    const list = (
      <Box component="ol" sx={{ m: 0, pl: 2.5, display: "grid", gap: 1 }}>
        {consoleSteps.map((step, index) => (
          <Typography
            key={index}
            component="li"
            variant="body2"
            sx={{ color: theme.customColors.text.strong, lineHeight: 1.6 }}
          >
            {renderInlineMarkdown(step)}
          </Typography>
        ))}
      </Box>
    );

    if (!collapsible) {
      return (
        <Box
          sx={{
            p: 2.5,
            borderRadius: "10px",
            border: `1px solid ${theme.customColors.border.subtle}`,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 2 }}>
            Console steps
          </Typography>
          {list}
        </Box>
      );
    }

    return (
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mt: 3,
          border: `1px solid ${theme.customColors.border.subtle}`,
          borderRadius: "10px !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            Console steps ({consoleSteps.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
          {list}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">Couldn't load remediation commands.</Alert>;
    }

    if (plan && !plan.hasCommand) {
      return (
        <>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: "8px" }}>
            Follow these steps in the cloud console, then re-run the check to
            confirm the fix.
          </Alert>
          {renderConsoleSteps({ collapsible: false })}
        </>
      );
    }

    // Some commands succeed and destroy unrelated state -- `--database-flags`
    // submits the instance's COMPLETE flag list, so remediating one flag resets
    // every other flag to its default. The command is correct as published, so
    // rather than rewriting it we withhold it until the user has read what it
    // will do. The console path stays visible here because it is usually the
    // non-destructive alternative.
    if (warningPending) {
      const { title, details = [], confirmLabel } = plan.warning;
      return (
        <>
          <Alert
            severity="warning"
            sx={{ mb: 2.5, borderRadius: "8px" }}
            icon={false}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
              {title || "Read this before running the command"}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {details.map((line, i) => (
                <Typography
                  key={i}
                  component="li"
                  sx={{ fontSize: 13, lineHeight: 1.65 }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          </Alert>

          {consoleSteps.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
                The console path below applies the same fix without this side
                effect.
              </Typography>
              {renderConsoleSteps({ collapsible: true })}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={() => setWarningAccepted(true)}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: theme.customColors.status.warning.main,
              color: theme.customColors.button.text,
              "&:hover": { bgcolor: theme.customColors.status.warning.text },
            }}
          >
            {confirmLabel || "I understand - show the command"}
          </Button>
        </>
      );
    }

    if (plan && !resources.length) {
      return <Alert severity="success">Nothing to remediate.</Alert>;
    }

    const accountHints = promptedVariables
      .map((item) => sharedHintFor(item.key))
      .filter(Boolean);

    return (
      <>
        {accountHints.map((hint) => (
          <Alert
            key={hint}
            severity="warning"
            sx={{ mb: 2, borderRadius: "8px" }}
          >
            {hint}
          </Alert>
        ))}

        {selected.size > 0 && promptedVariables.length > 0 && filtered.length > 1 && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: "8px",
              bgcolor: "#F3F4F6",
              border: "1px solid #E5E7EB",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selected.size} resource{selected.size > 1 ? "s" : ""} selected
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {saveState && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.customColors.text.subtle }}
                >
                  {saveState === "saving"
                    ? "Saving\u2026"
                    : saveState === "saved"
                    ? "Saved"
                    : "Couldn't save"}
                </Typography>
              )}
              <Button
                size="small"
                onClick={clearSelectedValues}
                sx={{ textTransform: "none" }}
              >
                Clear values
              </Button>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(240px, 1fr))",
                },
                alignItems: "start",
                gap: 1.5,
              }}
            >
              {promptedVariables.map((item) => (
                <TextField
                  key={item.key}
                  size="small"
                  fullWidth
                  select={choicesFor(item.key, bulkAccount).length > 0}
                  label={item.key}
                  placeholder={`Set for ${selected.size} selected`}
                  value={bulkValueFor(item.key)}
                  onChange={(event) => applyToSelected(item.key, event.target.value)}
                  helperText={item.description || item.note || " "}
                  sx={{
                    "& .MuiOutlinedInput-root": { bgcolor: "white" },
                    "& .MuiFormHelperText-root": {
                      mx: 0,
                      mt: 0.5,
                      lineHeight: 1.4,
                      overflowWrap: "anywhere",
                    },
                  }}
                  InputProps={{ sx: { ...monospace, borderRadius: "8px" } }}
                >
                  {choicesFor(item.key, bulkAccount).map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ ...monospace, fontSize: 12.5 }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              ))}
            </Box>
          </Box>
        )}

        {promptedVariables.length > 0 && filtered.length > 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: filtered.length > 1 ? 2 : 0.5,
              pb: filtered.length > 1 ? 1.5 : 0,
              borderBottom:
                filtered.length > 1
                  ? `1px solid ${theme.customColors.border.subtle}`
                  : "none",
            }}
          >
            {filtered.length > 1 && (
              <>
                <Checkbox
                  size="small"
                  // Covers every filtered resource, not just the page on screen
                  // -- a bulk value is nearly always meant for all of them.
                  checked={
                    filtered.length > 0 &&
                    filtered.every((r) => selected.has(idOf(r)))
                  }
                  indeterminate={
                    filtered.some((r) => selected.has(idOf(r))) &&
                    !filtered.every((r) => selected.has(idOf(r)))
                  }
                  onChange={(event) =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      filtered.forEach((r) =>
                        event.target.checked
                          ? next.add(idOf(r))
                          : next.delete(idOf(r))
                      );
                      return next;
                    })
                  }
                  sx={{ p: 0.5, ml: -0.5 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.subtle }}
                >
                  {selected.size > 0
                    ? `${selected.size} selected`
                    : "Select All"}
                </Typography>
              </>
            )}
          </Box>
        )}

        {(allAccounts.length > 1 ||
          resources.length > 8 ||
          (promptedVariables.length > 0 && filtered.length <= 1)) && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              mb: allAccounts.length > 1 || resources.length > 8 ? 2 : 0.5,
              mt: allAccounts.length > 1 || resources.length > 8 ? 0 : -1,
              alignItems: "center",
            }}
          >
            {allAccounts.length > 1 && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.subtle,
                    whiteSpace: "nowrap",
                  }}
                >
                  Showing resources in
                </Typography>
                <TextField
                  size="small"
                  select
                  value={accountFilter}
                  onChange={(event) => {
                    setAccountFilter(event.target.value);
                    setSelected(new Set());
                    setPage(1);
                  }}
                  sx={{ minWidth: 260 }}
                  InputProps={{ sx: { ...monospace, borderRadius: "8px" } }}
                >
                  {allAccounts.map((account) => (
                    <MenuItem
                      key={account}
                      value={account}
                      sx={{ ...monospace, fontSize: 12.5 }}
                    >
                      {account}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {resources.length > 8 && (
              <TextField
                size="small"
                sx={{ minWidth: 240, maxWidth: 360, flexShrink: 0 }}
                placeholder="Filter resources…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "8px" },
                }}
              />
            )}
          </Box>
        )}

        {visible.map((resource) => (
          <Box key={idOf(resource)} sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.75,
                minWidth: 0,
              }}
            >
              {filtered.length > 1 && (
                <Checkbox
                  size="small"
                  checked={selected.has(idOf(resource))}
                  onChange={() => toggleSelected(idOf(resource))}
                  sx={{ p: 0.5, ml: -0.5 }}
                />
              )}
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 13.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={resource.resourceId}
              >
                {resource.resourceName || resource.resourceId}
              </Typography>

              {/* The same resource name can exist in two accounts -- without
                  this the two rows are indistinguishable. Only shown when more
                  than one account is in play, so single-account checks stay
                  uncluttered. */}
              {multipleAccounts && resource.accountId && (
                <Typography
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontSize: 11.5,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: "4px",
                    backgroundColor: theme.customColors.background.subtle,
                    color: theme.customColors.text.subtle,
                    ...monospace,
                  }}
                  title={`Account ${resource.accountId}`}
                >
                  {resource.accountId}
                </Typography>
              )}

              <Box sx={{ flexGrow: 1 }} />
            </Box>

            {/* Inline, above the command it fills. */}
            {(fieldsByResource[idOf(resource)] || []).length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(auto-fit, minmax(220px, 1fr))",
                  },
                  gap: 1,
                  mb: 1,
                }}
              >
                {(fieldsByResource[idOf(resource)] || []).map((item) => {
                  const own = perResource[idOf(resource)]?.[item.key];
                  const shared = inputs[item.key] || "";
                  const options = choicesFor(
                    item.key,
                    resource.accountId,
                    idOf(resource)
                  );
                  return (
                    <TextField
                      key={item.key}
                      size="small"
                      fullWidth
                      select={options.length > 0}
                      label={item.key}
                      // Falls back to the bulk value so the field shows what
                      // the command actually used, and editing it splits.
                      value={own !== undefined ? own : shared}
                      placeholder={item.description || ""}
                      helperText={
                        sharedHintFor(item.key)
                          ? ""
                          : hintFor(resource.resourceId, item.key)
                      }
                      onChange={(event) => {
                        const value = event.target.value;
                        setPerResource((prev) => ({
                          ...prev,
                          [idOf(resource)]: {
                            ...(prev[idOf(resource)] || {}),
                            [item.key]: value,
                          },
                        }));
                      }}
                      InputProps={{
                        sx: {
                          ...monospace,
                          fontSize: 12.5,
                          borderRadius: "8px",
                        },
                      }}
                      sx={{
                        "& .MuiFormHelperText-root": {
                          mx: 0,
                          mt: 0.5,
                          lineHeight: 1.4,
                          overflowWrap: "anywhere",
                        },
                      }}
                    >
                      {options.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{ ...monospace, fontSize: 12.5 }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                })}
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Tooltip
              title={copiedKey === idOf(resource) ? "Copied" : "Copy command"}
            >
              <IconButton
                size="small"
                onClick={() => copyCommand(resource)}
                sx={{ mt: 0.25, flexShrink: 0 }}
              >
                {copiedKey === idOf(resource) ? (
                  <CheckIcon sx={{ fontSize: 17 }} color="success" />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
            <Box
              component="pre"
              sx={{
                ...monospace,
                flexGrow: 1,
                minWidth: 0,
                m: 0,
                p: 1.5,
                borderRadius: "8px",
                border: `1px solid ${theme.customColors.border.subtle}`,
                backgroundColor: theme.customColors.background.subtle,
                overflowX: "auto",
                whiteSpace: "pre",
                scrollbarWidth: "thin",
                scrollbarColor: "transparent transparent",
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: "transparent",
                  borderRadius: 4,
                },
                "&:hover": {
                  scrollbarColor: `${theme.customColors.border.subtle} transparent`,
                  "&::-webkit-scrollbar-thumb": {
                    background: theme.customColors.border.subtle,
                  },
                },
              }}
            >
              {resource.command}
            </Box>
            </Box>
          </Box>
        ))}

        {filtered.length > ROWS_PER_PAGE_OPTIONS[0] && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ py: 2 }}
          >
            <FormControl variant="outlined" size="small">
              <Select
                value={rowsPerPage}
                onChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(1);
                }}
                MenuProps={{
                  container: document.body,
                  style: { zIndex: 9999 },
                }}
              >
                {ROWS_PER_PAGE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Pagination
              count={pageCount}
              page={safePage}
              onChange={(_, value) => setPage(value)}
              variant="outlined"
              shape="rounded"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  sx={{
                    background: item.selected ? "#031B89 !important" : "",
                    color: item.selected ? "#fff !important" : "",
                  }}
                />
              )}
            />
          </Stack>
        )}

        {renderConsoleSteps({ collapsible: true })}
      </>
    );
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "58%" },
          maxWidth: 900,
          backgroundColor: theme.customColors.background.main,
        },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          px: 3,
          py: 2,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
            Remediate
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 3, py: 2.5 }}>
        {hasCommands && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: "8px" }}>
            {allAccounts.length > 1 ? (
              <>
                This check fails across{" "}
                <Box component="strong" sx={{ fontWeight: 600 }}>
                  {allAccounts.length} {accountWordPlural}
                </Box>
                . You get one script per {accountWord}, since each runs with its
                own credentials. Download them all now, or use the {accountWord}{" "}
                selector to review one at a time.
              </>
            ) : (
              <>
                Run these in your terminal with credentials for the affected{" "}
                {accountWord}, then re-run the check to confirm the fix.
              </>
            )}
          </Alert>
        )}
        {renderBody()}
      </Box>

      {hasCommands && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            px: 3,
            py: 2,
            backgroundColor: "#FFFFFF",
            borderTop: `1px solid ${theme.customColors.border.subtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.customColors.text.subtle }}
          >
            {totalReady === 0 ? "Fill in the values above to download" : ""}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {allAccounts.length > 1 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={downloading}
              onClick={() => requestDownload("current")}
              sx={{ textTransform: "none", borderRadius: "8px", mr: 1 }}
            >
              This {accountWord}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={downloading}
            onClick={() => requestDownload(allAccounts.length > 1 ? "all" : "current")}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            {allAccounts.length > 1
              ? `All ${accountWordPlural}`
              : selected.size > 0
              ? `Download ${selected.size} selected`
              : "Download script"}
          </Button>
        </Box>
      )}

      </Drawer>

      {/* Outside the Drawer: it renders in its own portal and would clip a
          fixed-position toast. zIndex must clear the Drawer's own layer. */}
      <LoadingToast
        open={downloading}
        title="Preparing your download"
        message={`Saving ${accountsWithWork.length} scripts, one per ${accountWord}.`}
        sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
      />

      <Dialog
        open={Boolean(pendingDownload)}
        onClose={() => setPendingDownload(null)}
        maxWidth="sm"
        sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            minWidth: 480,
            maxWidth: 620,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            pb: 2,
            pt: 2,
            px: 3,
          }}
        >
          Missing values
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 0,
            "&.MuiDialogContent-root": { paddingTop: 3 },
          }}
        >
          <Alert severity="info" sx={{ mb: 2, fontSize: "0.875rem", py: 0.5 }}>
            {(pendingDownload?.gaps || []).length === 1
              ? `This ${accountWord} has`
              : `These ${accountWordPlural} have`}{" "}
            commands left to fill. Download anyway and they arrive commented out
            at the end of the script, with their placeholders left in for you to
            fill manually.
          </Alert>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            {(pendingDownload?.gaps || []).map((gap) => (
              <Box
                key={gap.account || "single"}
                sx={{
                  border: `1px solid ${theme.customColors.border.subtle}`,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.25,
                }}
              >
                {gap.account && (
                  <Typography
                    variant="body2"
                    sx={{ ...monospace, fontWeight: 600, mb: 0.5 }}
                  >
                    {gap.account}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{ color: theme.customColors.text.subtle }}
                >
                  {gap.count} command{gap.count === 1 ? "" : "s"} missing:{" "}
                  {gap.keys.join(", ")}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pb: 2,
            pt: 2,
            borderTop: `1px solid ${theme.customColors.border.subtle}`,
            mt: 1,
          }}
        >
          <Button
            onClick={() => setPendingDownload(null)}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.strong,
              "&:hover": {
                borderColor: theme.customColors.border.strong,
                backgroundColor: theme.customColors.background.hover,
              },
            }}
          >
            Close
          </Button>
          <Button
            onClick={confirmDownload}
            variant="contained"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              bgcolor: theme.customColors.button.primary,
              color: theme.customColors.button.text,
              "&:hover": { bgcolor: theme.palette._blue.dark },
            }}
          >
            Download anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RemediationDrawer;
